import { RcFile } from 'antd/es/upload/interface';
import {
  LocalVideo,
  useCurrentConversationLocalVideo,
  UploadStateChangeEvent,
} from 'hooks/business/sendMessage';
import { get, set } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { uploadFile, uploadFileV2 } from 'services/api/resource';
import { message, Upload } from 'antd';
import validatorVideo, { getVideoThumbUrl } from 'utils/validator/video';
import { acceptVideoTypes } from 'utils/constants';
import { useTranslate } from '@tolgee/react';
import nimInst from 'services/nim';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import posthog from '@leyan/lytics';
import useIM from 'hooks/business/im/useIM';
import { uploadFile as uploadWaFile } from 'services/api/whatsapp';
import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { deleteResourceMessage, getResourceMessage } from 'services/cache/resourceMessage';
import { useMemoizedFn } from 'ahooks';
import { uploadLogInfoMap } from 'services/api/uploads';
import { logger } from 'services/logger';

const maxCount = 5;

export interface VideoFile extends File {
  width: number;
  height: number;
  uid: string;
}

const useUploadVideo = (
  textInputRef?: React.RefObject<TextAreaRef>,
  targetLang?: General.Language,
  onMercadoChatView?: boolean,
) => {
  const { currentConversation: conversation } = useChatConversationContext();
  const videoList = useCurrentConversationLocalVideo();

  const sendFileVideo = useSendFileVideo(onMercadoChatView);

  const onBeforeUpload = useCallback(
    (file: RcFile) => {
      textInputRef?.current?.focus();

      if (!conversation) {
        return Upload.LIST_IGNORE;
      }

      const { channel } = conversation;

      validatorVideo(channel, file, [0, 180])
        .then((validateRes) => {
          const { errorText, duration, size, width, height } = validateRes;

          if (errorText) {
            message.warn(errorText);

            posthog.capture('$invalidVideo', {
              error: {
                text: errorText,
                duration,
                size,
              },
            });

            return;
          }

          sendFileVideo(file, width, height, file.uid, targetLang);
        })
        .catch(() => {});

      return Upload.LIST_IGNORE;
    },
    [conversation, sendFileVideo, targetLang, textInputRef],
  );

  const uploadProps = useMemo(() => {
    return {
      name: 'file',
      maxCount,
      multiple: true,
      fileList: [],
      accept: acceptVideoTypes.toString(),
    };
  }, []);

  return {
    videoList,
    onBeforeUpload,
    uploadProps,
  };
};

export function useSendFileVideo(onMercadoChatView = false) {
  const { currentConversation: conversation } = useChatConversationContext();

  const { t } = useTranslate();

  const { sendVideo, sendAttachment, sendShopeeVideo } = useIM();

  // 上传到 whatsapp
  const upload2Wa = useMemoizedFn(
    (
      file: File,
      conversation: Conversation.Conversation,
      width: number,
      height: number,
      resolve,
      reject,
    ) => {
      const { conversationId } = conversation;
      uploadWaFile(file)
        .then((res) => {
          resolve({
            status: 4,
            conversationId,
            localId: res.url,
            name: file.name,
            resourceId: res.url,
            videoContent: {
              videoUrl: res.url,
              thumbUrl: res.thumbUrl,
              durationSeconds: res.videoDuration,
              thumbWidth: width,
              thumbHeight: height,
              // videoId: res.url,
              videoName: file.name,
              localVideoUrl: res.localUrl,
              resourceId: res.url,
            },
          });
        })
        .catch(reject);
    },
  );

  // 上传到平台
  const upload2Platform = useMemoizedFn(
    async (
      file: File,
      conversation: Conversation.Conversation,
      uid: string,
      width: number,
      height: number,
      resolve,
      reject,
    ) => {
      const { channel, storeId, region, conversationId, topBarInfo } = conversation;
      try {
        const uploadRes =
          channel === 'SHOPEE'
            ? await uploadFileV2({
                storeId,
                region,
                file,
                externalConversationId: topBarInfo?.externalConversationId,
              })
            : await uploadFile({
                storeId,
                region,
                file,
                externalConversationId: topBarInfo?.externalConversationId,
              });
        logger.info(
          `[ResourceUpload]: ${JSON.stringify({
            type: 'httpResponse',
            data: uploadRes,
          })}`,
        );
        const {
          resourceId,
          state,
          externalVideoId = '',
          videoUrl,
          thumbUrl,
          durationSeconds,
          localVideoUrl,
        } = uploadRes;

        if (state === 5) {
          const error = new Error('http state === 5');

          set(error, 'failCause', {
            failType: 'A',
            code: '',
            desc: '',
            failMsgMap: {
              'zh-CN': t('ViewsChat.UPLOAD_TO_PLATFORM_FAILED'),
              'en-US': t('ViewsChat.UPLOAD_TO_PLATFORM_FAILED'),
            },
          } as Conversation.FailContent);

          reject(error);

          return;
        }

        const videoContent: LocalVideo['videoContent'] = {
          videoUrl: videoUrl || localVideoUrl,
          thumbUrl,
          durationSeconds,
          thumbWidth: width,
          thumbHeight: height,
          videoId: externalVideoId,
          videoName: file.name,
          localVideoUrl,
          resourceId,
          vid: externalVideoId,
        };

        const localVideo = {
          status: state,
          conversationId,
          localId: uid,
          name: file.name,
          resourceId,
          videoContent,
        };

        if (state === 4) {
          resolve(localVideo);

          return;
        }

        const handleMessage = (message: any, handler?: (message: any) => void) => {
          const {
            resourceId,
            resourceState,
            url,
            externalVideoId = '',
          } = (message as UploadStateChangeEvent) || {};

          if (!resourceId || String(resourceId) !== String(uploadRes.resourceId)) {
            return false;
          }

          logger.info(
            `[ResourceUpload]: ${JSON.stringify({
              type: 'yunxinMessage-useUploadVideo',
              data: message,
            })}`,
          );

          const uploadInfo = uploadLogInfoMap[resourceId];
          delete uploadLogInfoMap[resourceId];

          if (resourceState === 4 || resourceState === 8) {
            resolve({
              ...localVideo,
              videoContent: {
                ...videoContent,
                videoUrl: url || videoContent.videoUrl,
                videoId: externalVideoId,
                vid: externalVideoId,
              },
            });

            handler?.(message);

            if (uploadInfo) {
              const uploadPlatformTime = Date.now();
              const uploadPlatformDuration = uploadPlatformTime - uploadInfo.uploadOssTime;
              posthog.capture('$uploadInfo', {
                ...uploadInfo,
                uploadPlatformTime,
                uploadPlatformDuration,
              });
            }

            return true;
          }

          if (resourceState === 5 || resourceState === 12 || resourceState === 14) {
            const error = new Error(`message resourceState: ${resourceState}`);
            const failMsg = {
              5: {
                'zh-CN': t('ViewsChat.UPLOAD_TO_PLATFORM_FAILED'),
                'en-US': t('ViewsChat.UPLOAD_TO_PLATFORM_FAILED'),
              },
              12: {
                'zh-CN': t('ViewsChat.上传失败'),
                'en-US': t('ViewsChat.上传失败'),
              },
              14: {
                'zh-CN': '视频接口已升级，需重新授权店铺后使用该功能',
                'en-US':
                  'Video interface has been upgraded, please re-authorize the store after using this feature',
              },
            } as const;

            set(error, 'failCause', {
              failType: 'A',
              code: 'upload_failed',
              desc: '',
              failMsgMap: failMsg[resourceState],
            } as Conversation.FailContent);

            reject(error);

            handler?.(message);

            return true;
          }

          return false;
        };

        const handled = handleMessage(getResourceMessage(uploadRes.resourceId), (message) => {
          logger.info(
            `[ResourceUpload]: ${JSON.stringify({
              type: 'yunxin-before-http',
              data: message,
            })}`,
          );
        });

        deleteResourceMessage(uploadRes.resourceId);

        if (handled) {
          return;
        }

        let timer: any;

        const off = nimInst.on('message', (message: any) =>
          handleMessage(message, () => {
            off();

            clearTimeout(timer);
          }),
        );

        timer = setTimeout(() => {
          off();

          const error = new Error('Timeout');

          set(error, 'failCause', {
            failType: 'A',
            code: '',
            desc: '',
            failMsgMap: {
              'zh-CN': '上传超时',
              'en-US': 'Upload timed out',
            },
          } as Conversation.FailContent);

          reject(error);
        }, 20 * 60 * 1000);
      } catch (err) {
        const msg = get(err, 'response.data.msg');

        const error = new Error(err);

        const failMsg =
          msg === 'shoppe erp access_token is expired'
            ? t('ViewsChat.该店授权已失效')
            : t('ViewsChat.上传失败');

        set(error, 'failCause', {
          failType: 'A',
          code: '',
          desc: '',
          failMsgMap: {
            'zh-CN': failMsg,
            'en-US': failMsg,
          },
        } as Conversation.FailContent);

        reject(error);
      }
    },
  );

  const uploadPromiseCatch = useMemoizedFn((err) => {
    posthog.capture('$uploadFailed', {
      type: 'uploadVideo',
      error: String(err),
    });

    return Promise.reject(err);
  });

  // // Shopee 发送视频
  const sendVideoOnShopee = useMemoizedFn(
    (
      file: File,
      conversation: Conversation.Conversation,
      uid: string,
      width: number,
      height: number,
      thumbUrl: string,
      targetLang?: General.Language,
    ) => {
      const { conversationId } = conversation;
      const promise = new Promise<Util.PartialRequired<LocalVideo, 'videoContent'>>(
        (resolve, reject) => {
          upload2Platform(file, conversation, uid, width, height, resolve, reject);
        },
      )
        .then(({ videoContent }) => {
          return {
            ...videoContent,
            contentLength: file.size,
          };
        })
        .catch(uploadPromiseCatch);
      sendShopeeVideo(conversationId, promise, thumbUrl, targetLang);
    },
  );

  // whatsapp 发送视频
  const sendVideoOnWa = useMemoizedFn(
    (
      file: File,
      conversation: Conversation.Conversation,
      width: number,
      height: number,
      thumbUrl: string,
    ) => {
      const { conversationId } = conversation;
      const promise = new Promise<Util.PartialRequired<LocalVideo, 'videoContent'>>(
        (resolve, reject) => {
          upload2Wa(file, conversation, width, height, resolve, reject);
        },
      )
        .then((res) => {
          const {
            videoContent: { thumbUrl, videoUrl, videoId = '', thumbWidth, thumbHeight },
          } = res;

          return {
            videoId,
            videoUrl,
            thumbUrl,
            thumbWidth,
            thumbHeight,
          };
        })
        .catch(uploadPromiseCatch);

      sendVideo(conversationId, promise, thumbUrl);
    },
  );

  // 各个平台 发送视频
  const sendVideoOnPlatform = useMemoizedFn(
    (
      file: File,
      conversation: Conversation.Conversation,
      uid: string,
      width: number,
      height: number,
      thumbUrl: string,
    ) => {
      const { conversationId } = conversation;
      const promise = new Promise<Util.PartialRequired<LocalVideo, 'videoContent'>>(
        (resolve, reject) => {
          upload2Platform(file, conversation, uid, width, height, resolve, reject);
        },
      )
        .then((res) => {
          const {
            videoContent: {
              thumbUrl,
              videoUrl,
              videoId = '',
              thumbWidth,
              thumbHeight,
              localVideoUrl,
              durationSeconds,
              resourceId,
            },
          } = res;

          return {
            videoUrl,
            thumbUrl,
            thumbWidth,
            thumbHeight,
            localVideoUrl,
            contentLength: file.size,
            durationSeconds,
            resourceId,
            videoId,
            externalId: videoId,
            mimeType: file.type,
          };
        })
        .catch(uploadPromiseCatch);
      sendVideo(conversationId, promise, thumbUrl);
    },
  );

  // mercado 发送视频
  const sendVideoOnMercado = useMemoizedFn(
    async (file: File, conversation: Conversation.Conversation, targetLang?: General.Language) => {
      const { region } = conversation;
      try {
        const data = await uploadFile({
          region,
          file,
        });
        const text = `Clique para ver o vídeo ${data.localVideoUrl}`;
        sendAttachment(
          conversation,
          {
            text,
            originalText: text,
            subject: '',
            attachments: [],
          },
          undefined,
          onMercadoChatView,
        );
      } catch (err) {
        return uploadPromiseCatch(err);
      }
    },
  );

  return useCallback(
    (file: File, width: number, height: number, uid: string, targetLang?: General.Language) => {
      if (!conversation) {
        return;
      }

      const { conversationId, channel, storeId } = conversation;

      posthog.capture('$startUpload', {
        type: 'uploadVideo',
        data: {
          conversationId,
          storeId,
        },
      });

      const videoUrl = URL.createObjectURL(file);

      getVideoThumbUrl(videoUrl)
        .catch(() => '')
        .then((thumbUrl) => {
          if (channel === 'SHOPEE') {
            sendVideoOnShopee(file, conversation, uid, width, height, thumbUrl);
          } else if (channel === 'WA_BIZ') {
            sendVideoOnWa(file, conversation, width, height, thumbUrl);
          } else if (channel === 'MERCADO') {
            sendVideoOnMercado(file, conversation, targetLang);
          } else {
            sendVideoOnPlatform(file, conversation, uid, width, height, thumbUrl);
          }
        })
        .finally(() => {
          URL.revokeObjectURL(videoUrl);
        });
    },
    [conversation, sendVideoOnShopee, sendVideoOnWa, sendVideoOnMercado, sendVideoOnPlatform],
  );
}

export default useUploadVideo;
